//VisualBriefDownload.jsx
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import Loader from '../../components/utils/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faDownload, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import { jsPDF } from "jspdf";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Set up pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const VisualBriefDownload = () => {
    const location = useLocation();
    const authState = useSelector(state => state.authReducer);
    const navigate = useNavigate();
    const [fetchData, { loading }] = useFetch();
    const [vbMasterId, setVbMasterId] = useState(location.state?.vbMasterId || '');
    const [vbTitle, setVbTitle] = useState(location.state?.title || '');
    const [noOfSlides, setNoOfSlides] = useState(location.state?.noOfSlides || 0);
    const [successMessage, setSuccessMessage] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const previewRef = useRef(null);

    // console.log("vbMasterId: ",vbMasterId);

    // Define user details from authState
    const userId = authState.user?._id || '';
    const userName = authState.user?.userName || authState.user?.name || 'Unknown User';
    const userCode = authState.user?.userCode || ''; // Assuming userCode might exist; set to empty if not

    useEffect(() => {
        setVbMasterId(location.state?.vbMasterId || '');
        setVbTitle(location.state?.title || '');
        setNoOfSlides(location.state?.noOfSlides || 0);
    }, [location.state]);

    // Cleanup blob URL on unmount
    useEffect(() => {
        return () => {
            if (pdfBlob) {
                URL.revokeObjectURL(pdfBlob);
            }
        };
    }, [pdfBlob]);

    const [vbSlides, setSlides] = useState([]);
    const [vbComments, setVBComments] = useState([]);

    const fetchSlidesList = useCallback(() => {
        const config = {
            url: "/vbslides/allslides",
            method: "post",
            headers: { Authorization: authState.token },
            data: { vbMasterId: vbMasterId }
        };
        fetchData(config, { showSuccessToast: false })
            .then(response => {
                if (Array.isArray(response)) {
                    setSlides(response);
                }
                else {
                    console.error("Slides not found!");
                }
            })
            .catch(error => {
                console.error("Error fetching slides: ", error);
            });
    }, [authState.token, fetchData, vbMasterId]);

    const fetchComments = useCallback(() => {
        const config = {
            url: "/vbslidecomments/all",
            method: "post",
            headers: { Authorization: authState.token },
            data: { vbMasterId: vbMasterId }
        };
        fetchData(config, { showSuccessToast: false })
            .then(response => {
                    // console.log("ppp...",response);
                    setVBComments(response.vbslideComments);
            })
            .catch(error => {
                console.error("Error fetching comments: ", error);
            });
    }, [authState.token, fetchData, vbMasterId]);

    useEffect(() => {
        fetchSlidesList();
        fetchComments()
    }, [fetchSlidesList,fetchComments]);

    useEffect(() => {
    }, [vbComments]);

    // Handle full screen toggle
    const toggleFullScreen = () => {
        if (!isFullScreen) {
            if (previewRef.current?.requestFullscreen) {
                previewRef.current.requestFullscreen();
            } else if (previewRef.current?.webkitRequestFullscreen) {
                previewRef.current.webkitRequestFullscreen();
            } else if (previewRef.current?.msRequestFullscreen) {
                previewRef.current.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
        setIsFullScreen(!isFullScreen);
    };

    // Handle fullscreen change
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement));
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Function to generate PDF blob for preview
    const handleGeneratePreview = async () => {
        const doc = new jsPDF();
        let yPosition = 10; 
        const pageHeight = 280; 
    
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(`Visual Brief: ${vbTitle}`, 10, yPosition);
        yPosition += 10;
    
        for (const slide of vbSlides) {
            if (yPosition > pageHeight - 30) {
                doc.addPage();
                yPosition = 10;
            }
    
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text(`${slide.title}`, 10, yPosition);
            yPosition += 6;
    
            if (slide.slideDesc) {
                doc.setFont("helvetica", "normal");
                let splitDesc = doc.splitTextToSize(slide.slideDesc, 180);
                doc.text(splitDesc, 10, yPosition);
                yPosition += splitDesc.length * 6;
            }
    
            if (slide.websiteUrl) {
                doc.setTextColor(0, 0, 255);
                doc.textWithLink("Website Link", 10, yPosition, { url: slide.websiteUrl });
                yPosition += 6;
            }
    
            if (slide.youtubeUrl) {
                doc.setTextColor(255, 0, 0);
                doc.textWithLink("YouTube Link", 10, yPosition, { url: slide.youtubeUrl });
                yPosition += 6;
            }
    
            doc.setTextColor(0, 0, 0);
            yPosition += 10;
    
            // Add related comments
            const slideComments = vbComments.filter(comment => comment.vbSlideTitle === slide.title);
            if (slideComments.length > 0) {
                doc.setFont("helvetica", "bold");
                doc.text("Comments:", 10, yPosition);
                yPosition += 6;
    
                for (const comment of slideComments) {
                    if (yPosition > pageHeight - 30) {
                        doc.addPage();
                        yPosition = 10;
                    }
    
                    doc.setFont("helvetica", "normal");
                    let commentText = `By: ${comment.userName} - ${new Date(comment.dateCommented).toLocaleDateString()}`;
                    doc.text(commentText, 10, yPosition);
                    yPosition += 6;
    
                    let commentContent = doc.splitTextToSize(`"${comment.userComment}"`, 180);
                    doc.text(commentContent, 10, yPosition);
                    yPosition += commentContent.length * 6;
                }
    
                yPosition += 10; 
            }
    
            // ✅ Convert Image to Base64 Before Adding to PDF
            if (slide.slideImageData) {
                try {
                    const imgURL = `/api/downloads/visualbrief/${slide.slideImageData}.png`;
    
                    // Fetch image and convert to Base64
                    const toBase64 = async (url) => {
                        const response = await fetch(url);
                        if (!response.ok) throw new Error("Image fetch failed");
                        const blob = await response.blob();
                        return new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.readAsDataURL(blob);
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = reject;
                        });
                    };
    
                    const base64Image = await toBase64(imgURL);
    
                    // If space runs out, move to a new page
                    if (yPosition > pageHeight - 50) {
                        doc.addPage();
                        yPosition = 10;
                    }
    
                    doc.addImage(base64Image, "PNG", 10, yPosition, 50, 30);
                    yPosition += 40;
                } catch (error) {
                    console.error("Error loading image:", error);
                }
            }
        }
    
        // Generate blob for preview
        const pdfBlob = doc.output('blob');
        setPdfBlob(pdfBlob);
        setShowPreview(true);
        setPageNumber(1);
    };

    // Function to download PDF and save log
    const handleDownloadPDF = () => {
        if (pdfBlob) {
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `VisualBrief_${vbTitle}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            saveDownloadLog();
        }
    };

    // Save Download Log
    const saveDownloadLog = async () => {
        const now = new Date();
        const localDateString = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString();
        try {
            const config = {
                url: "/vbdownloaded",
                method: "post",
                headers: { Authorization: authState.token },
                data: {
                    vbMasterId: vbMasterId,
                    vbMasterTitle: vbTitle,
                    userId: userId,
                    dateDownloaded: localDateString,
                    userCode: userCode,
                    userName: userName,
                    isActive: true,
                }
            };
            await fetchData(config, { showSuccessToast: false });
            setSuccessMessage(true);
        } catch (error) {
            console.error("Error while saving download log:", error);
        }
    };

    // Page navigation handlers
    const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
    const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages || 1));

    // Touch handlers for swipe gestures
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        previewRef.current.touchStartX = touch.clientX;
        previewRef.current.touchStartY = touch.clientY;
    };

    const handleTouchMove = (e) => {
        if (!previewRef.current.touchStartX) return;
        const touch = e.touches[0];
        const deltaX = touch.clientX - previewRef.current.touchStartX;
        const deltaY = touch.clientY - previewRef.current.touchStartY;
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
            if (deltaX > 0 && pageNumber > 1) {
                goToPrevPage();
            } else if (deltaX < 0 && pageNumber < numPages) {
                goToNextPage();
            }
            previewRef.current.touchStartX = null;
        }
    };

    const handleTouchEnd = () => {
        previewRef.current.touchStartX = null;
    };

    return (
        <div>
            <div className='py-4 px-4 bg-black'>
                <header className='flex bg-black border border-teal-500 px-2 py-2 mb-2'>
                    <h1 className='text-lg lg:text-xl py-2 px-2 text-yellow-200'>Visual Brief</h1>
                </header>
                <div className='flex flex-col bg-gray-700 px-2 py-2'>
                    <h3 className='text-lg py-2 px-2 text-white  '>{vbTitle} Presentation Download</h3>
                    <div className="flex gap-2">
                        {!showPreview ? (
                            <button 
                                className="bg-blue-500 text-white px-2 py-2 rounded-md w-48"
                                onClick={handleGeneratePreview}
                            >
                                <FontAwesomeIcon icon={faEye} className="mr-2" />Generate Preview
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="bg-green-500 text-white px-2 py-2 rounded-md w-32"
                                    onClick={handleDownloadPDF}
                                >
                                    <FontAwesomeIcon icon={faDownload} className="mr-2" />Download PDF
                                </button>
                                <button 
                                    className="bg-gray-500 text-white px-2 py-2 rounded-md w-20"
                                    onClick={() => {
                                        setShowPreview(false);
                                        setPdfBlob(null);
                                        setPageNumber(1);
                                        setNumPages(null);
                                    }}
                                >
                                    Close
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {loading ? (
                    <Loader />
                ) : (
                    <div>
                        {vbSlides.length === 0 ? (
                            <div className='w-[600px] h-[300px] flex align-middle items-center justify-center text-yellow-300'>
                                No records found!
                            </div>
                        ) : (
                            <>
                                <div className="mb-4">
                                    {vbSlides.map((slide, index) => (
                                        <div key={slide._id} className='bg-gray-600 my-1 p-4 text-white rounded-md shadow-md'>
                                            <div className='flex'>
                                                <div className='font-medium'>{slide.slideNumber} {'.'} {slide.title}</div>
                                            </div>
                                            <p><strong>Description:</strong> {slide.slideDesc}</p>
                                        </div>
                                    ))}
                                </div>
                                {showPreview && pdfBlob && (
                                    <div 
                                        ref={previewRef}
                                        className={`mb-4 border border-gray-400 rounded-lg overflow-hidden shadow-lg relative touch-manipulation ${
                                            isFullScreen ? 'fixed inset-0 z-50 bg-black' : ''
                                        }`}
                                        onTouchStart={handleTouchStart}
                                        onTouchMove={handleTouchMove}
                                        onTouchEnd={handleTouchEnd}
                                    >
                                        {isFullScreen && (
                                            <button 
                                                className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full"
                                                onClick={toggleFullScreen}
                                            >
                                                <FontAwesomeIcon icon={faCompress} />
                                            </button>
                                        )}
                                        <div className={`p-4 overflow-auto h-full ${isFullScreen ? 'p-8' : 'h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px]'}`}>
                                            <Document 
                                                file={pdfBlob} 
                                                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                                                loading={<div className="flex justify-center items-center h-64"><Loader /></div>}
                                                className="flex flex-col items-center"
                                            >
                                                <Page 
                                                    pageNumber={pageNumber} 
                                                    width={Math.min(800, window.innerWidth - 32)} // Responsive width
                                                    renderTextLayer={true}
                                                    renderAnnotationLayer={true}
                                                    className="mb-4 shadow-md"
                                                />
                                            </Document>
                                            {numPages > 1 && (
                                                <div className="flex justify-center items-center gap-4 mt-4">
                                                    <button 
                                                        className="bg-blue-500 text-white w-20 px-4 py-2 rounded-md disabled:opacity-50"
                                                        onClick={goToPrevPage}
                                                        disabled={pageNumber <= 1}
                                                    >
                                                        Previous
                                                    </button>
                                                    <span className="text-white">
                                                        Page {pageNumber} of {numPages}
                                                    </span>
                                                    <button 
                                                        className="bg-blue-500 text-white w-20 px-4 py-2 rounded-md disabled:opacity-50"
                                                        onClick={goToNextPage}
                                                        disabled={pageNumber >= numPages}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {!isFullScreen && (
                                            <button 
                                                className="absolute top-2 right-2 z-10 bg-black/50 text-white p-2 rounded-full"
                                                onClick={toggleFullScreen}
                                            >
                                                <FontAwesomeIcon icon={faExpand} />
                                            </button>
                                        )}
                                    </div>
                                )}
                                <div className='mt-4 mb-4 px-2 py-2'>
                                    <p>Presentation {vbTitle} Comments:</p>
                                    {vbComments.map((comment, index) => (
                                        <div key={comment._id} className='bg-gray-700 my-1 p-4 text-white rounded-md shadow-md'>
                                            <p><strong>Slide:</strong> {comment.vbSlideTitle}</p>
                                            <p><strong>By:</strong> {comment.userName}</p>
                                            <p><strong>Comment:</strong> {comment.userComment}</p>
                                            <p><strong>Date:</strong> {new Date(comment.dateCommented).toLocaleDateString()}</p>
                                        </div>
                                    ))}
                                </div>
                                {successMessage && (
                                    <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                                        Download log saved successfully!
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisualBriefDownload;